import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  collection, getDocs, updateDoc, addDoc, deleteDoc, doc,
  serverTimestamp, onSnapshot, query, where,
  orderBy, getDoc, setDoc
} from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';
import AddItemModal from '../components/AddItemModal';
import ComboBuilderModal from '../components/ComboBuilderModal';
import { printReceipt } from '../utils/printReceipt';
import { printKOT } from '../utils/printKOT';
import { sendOrderReadyWhatsApp } from '../utils/whatsapp';
import { fmt, round2, autoSizePrice, GST_RATE } from '../utils/money';
import { optionLabel, cleanAddons } from '../utils/addons';
import { DEFAULT_UPSELL_ITEMS } from '../utils/upsell';
import { expiryDaysFrom, lotsFromDoc, activeLots, balanceOf, redeemFifo, compactLots, nextExpiry, fmtDate } from '../utils/loyalty';
import toast from 'react-hot-toast';

function cleanForFirestore(obj) {
  if (Array.isArray(obj)) return obj.filter(v => v !== undefined).map(cleanForFirestore);
  if (obj !== null && typeof obj === 'object') {
    const proto = Object.getPrototypeOf(obj);
    if (proto !== Object.prototype && proto !== null) return obj; // Timestamp, serverTimestamp(), etc.
    const cleaned = {};
    for (const [key, value] of Object.entries(obj)) cleaned[key] = value === undefined ? null : cleanForFirestore(value);
    return cleaned;
  }
  return obj;
}


const TODAY_DAY = new Date().getDay();
const SIZE_MULT = { Personal:1, Share:1.70, Family:2.80 };

const LOYALTY_MIN_ORDER   = 200;
const LOYALTY_CAP_NORMAL  = 0.10;
const LOYALTY_CAP_W_OFFER = 0.05;
const POINTS_PER_RUPEE_REDEEM = 10;
const SLOT_COUNT = 24; // number of table/order tiles shown per order type — change here if needed

async function getNextBillNumber() {
  const today = new Date().toISOString().slice(0,10);
  const ref   = doc(db,'config',`billCounter_${today}`);
  const snap  = await getDoc(ref);
  const next  = snap.exists() ? (snap.data().counter||0)+1 : 1;
  await setDoc(ref,{counter:next,date:today});
  return `VIP${String(next).padStart(2,'0')}`;
}

function elapsedLabel(startedAt) {
  const start = startedAt?.toDate?.();
  if (!start) return '';
  const mins = Math.floor((Date.now() - start.getTime()) / 60000);
  const h = Math.floor(mins/60), m = mins%60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

// ─── QUANTITY BOX (− [ 2 ] +) shown in front of each cart item ───
function QtyBox({ value, onCommit }) {
  const [txt, setTxt] = useState(String(value));
  useEffect(() => { setTxt(String(value)); }, [value]);
  const commit = () => {
    const n = Math.max(0, Math.floor(Number(txt) || 0));
    if (n !== value) onCommit(n); else setTxt(String(value));
  };
  const btn = { width:26, height:26, border:'none', background:'#f5f5f5', cursor:'pointer', fontSize:16, lineHeight:1, color:'#555', flexShrink:0 };
  return (
    <div style={{ display:'flex', alignItems:'center', border:'1px solid #e3e3e3', borderRadius:7, overflow:'hidden', flexShrink:0, width:92 }}>
      <button type="button" style={btn} onClick={() => onCommit(Math.max(0, value - 1))}>−</button>
      <input value={txt} inputMode="numeric" aria-label="Quantity"
        onChange={e => setTxt(e.target.value.replace(/\D/g, ''))}
        onBlur={commit}
        onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); }}
        style={{ width:38, height:26, textAlign:'center', border:'none', outline:'none', fontSize:13, fontWeight:700, minWidth:0 }} />
      <button type="button" style={btn} onClick={() => onCommit(value + 1)}>+</button>
    </div>
  );
}

// ─── OFFER PIZZA MODAL (fixed-price weekly offers) ────────────
function OfferPizzaModal({ offer, addonGroups, pizzaItems, onClose, onAdd }) {
  const [selected, setSelected] = useState({});
  const eligible = offer.catFilter?.length ? pizzaItems.filter(i => offer.catFilter.includes(i.cat)) : pizzaItems;
  const toggle = (item) => setSelected(p => { if(p[item.id]){const n={...p};delete n[item.id];return n;} return {...p,[item.id]:{item,addons:{}}}; });
  const toggleAddon = (pid,gid,oid,stype) => setSelected(p=>{
    const pz=p[pid]; if(!pz) return p;
    const cur=pz.addons[gid];
    const next = stype==='single' ? (cur===oid?null:oid) : (cur||[]).includes(oid)?(cur||[]).filter(x=>x!==oid):[...(cur||[]),oid];
    return {...p,[pid]:{...pz,addons:{...pz.addons,[gid]:next}}};
  });
  const getPrice = (opt) => offer.sizeFilter==='Family' ? opt.familyPrice||0 : offer.sizeFilter==='Share' ? opt.sharePrice||0 : opt.personalPrice||0;
  const addonAmt = (addons) => { let t=0; addonGroups.forEach(g=>{ const sel=addons[g.id]; if(!sel) return; const ids=g.selectionType==='single'?(sel?[sel]:[]):(sel||[]); ids.forEach(oid=>{const o=(g.options||[]).find(x=>x.id===oid);if(o)t+=getPrice(o);}); }); return t; };
  const addonNames = (addons) => { const n=[]; addonGroups.forEach(g=>{ const sel=addons[g.id];if(!sel)return; const ids=g.selectionType==='single'?(sel?[sel]:[]):(sel||[]); ids.forEach(oid=>{const o=(g.options||[]).find(x=>x.id===oid);if(o)n.push(optionLabel(o));}); }); return n; };
  const addAll = () => {
    const items=Object.values(selected);
    if(!items.length){toast.error('Select at least one pizza');return;}
    items.forEach(({item,addons})=>{
      onAdd({ menuId:item.id, name:`${item.name} (${offer.sizeFilter})`, price:offer.fixedPrice+addonAmt(addons), qty:1,
               addons:addonNames(addons), sizeLabel:offer.sizeFilter, sizeBasePrice:offer.fixedPrice, offerApplied:true });
    });
    onClose();
  };
  const cnt   = Object.keys(selected).length;
  const total = Object.values(selected).reduce((s,{addons})=>s+offer.fixedPrice+addonAmt(addons),0);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div style={{background:'#fff',borderRadius:14,padding:20,width:560,maxHeight:'85vh',overflowY:'auto',boxShadow:'0 8px 30px rgba(0,0,0,.18)'}} onClick={e=>e.stopPropagation()}>
        <div style={{fontWeight:700,fontSize:16,marginBottom:3}}>{offer.name}</div>
        <div style={{fontSize:13,color:'#888',marginBottom:14}}>{offer.description}</div>
        {eligible.map(item=>{
          const isSel=!!selected[item.id]; const pz=selected[item.id];
          return (
            <div key={item.id} style={{border:`1.5px solid ${isSel?'#D85A30':'#eee'}`,borderRadius:10,marginBottom:8,overflow:'hidden'}}>
              <div onClick={()=>toggle(item)} style={{display:'flex',alignItems:'center',gap:12,padding:'10px 14px',background:isSel?'#FAECE7':'#fff',cursor:'pointer'}}>
                <div style={{width:22,height:22,borderRadius:4,border:`2px solid ${isSel?'#D85A30':'#ddd'}`,background:isSel?'#D85A30':'#fff',display:'flex',alignItems:'center',justifyContent:'center',color:'#fff',fontSize:14,flexShrink:0}}>{isSel&&'✓'}</div>
                <span style={{fontSize:20}}>{item.emoji}</span>
                <div style={{flex:1}}><div style={{fontWeight:500,fontSize:13}}>{item.name}</div><div style={{fontSize:11,color:'#888'}}>{item.cat}</div></div>
                <div style={{textAlign:'right'}}>
                  <div style={{fontWeight:700,color:'#D85A30'}}>₹{offer.fixedPrice}</div>
                  {pz&&addonAmt(pz.addons)>0&&<div style={{fontSize:11,color:'#888'}}>+₹{addonAmt(pz.addons)} addons</div>}
                </div>
              </div>
              {isSel && addonGroups.filter(g=>(g.appliesTo||['pizza']).includes('pizza')).map(g=>{
                const opts=(g.options||[]).filter(o=>o.active!==false); if(!opts.length) return null;
                return (
                  <div key={g.id} style={{padding:'8px 14px 8px 48px',background:'#fafafa',borderTop:'1px solid #f0f0f0'}}>
                    <div style={{fontSize:11,fontWeight:600,color:'#888',marginBottom:5}}>{g.name} <span style={{fontWeight:400}}>({g.selectionType==='single'?'Choose one':'Choose many'})</span></div>
                    <div style={{display:'flex',flexWrap:'wrap',gap:5}}>
                      {opts.map(opt=>{
                        const pr=getPrice(opt); const sel=pz.addons[g.id]; const active=g.selectionType==='single'?sel===opt.id:(sel||[]).includes(opt.id);
                        return (<div key={opt.id} onClick={e=>{e.stopPropagation();toggleAddon(item.id,g.id,opt.id,g.selectionType);}} style={{padding:'4px 9px',borderRadius:20,fontSize:11,cursor:'pointer',border:`1px solid ${active?'#D85A30':'#eee'}`,background:active?'#FAECE7':'#fff',color:active?'#993C1D':'#555'}}>{optionLabel(opt)} {pr>0?`+₹${pr}`:'Free'}</div>);
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })}
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginTop:14,paddingTop:14,borderTop:'1px solid #eee'}}>
          <div style={{fontSize:14}}><b>{cnt}</b> pizza{cnt!==1?'s':''} selected{cnt>0&&<span style={{color:'#D85A30',marginLeft:8}}>Total: ₹{total}</span>}</div>
          <div style={{display:'flex',gap:8}}><button className="btn" onClick={onClose}>Cancel</button><button className="btn btn-primary" onClick={addAll} disabled={!cnt}>Add {cnt||''} to Bill →</button></div>
        </div>
      </div>
    </div>
  );
}

// ─── FREE PIZZA PICKER (buy1get1 weekly offers) ───────────────
function FreePizzaPickerModal({ entitlements, addonGroups, pizzaItems, onClose, onAdd }) {
  const [selections, setSelections] = useState({});
  const slots = [];
  entitlements.forEach(ent=>{ for(let i=0;i<ent.count;i++) slots.push({key:`${ent.size}_${i}`,size:ent.size,maxPrice:ent.maxPrice}); });
  const selectForSlot = (key,item) => setSelections(p=>{ if(p[key]?.item?.id===item.id) return {...p,[key]:null}; return {...p,[key]:{item,addons:{}}}; });
  const toggleAddon = (key,gid,oid,stype) => setSelections(p=>{ const slot=p[key];if(!slot)return p; const cur=slot.addons[gid]; const next=stype==='single'?(cur===oid?null:oid):(cur||[]).includes(oid)?(cur||[]).filter(x=>x!==oid):[...(cur||[]),oid]; return {...p,[key]:{...slot,addons:{...slot.addons,[gid]:next}}}; });
  const getSizePrice = (opt,size) => size==='Family'?opt.familyPrice||0:size==='Share'?opt.sharePrice||0:opt.personalPrice||0;
  const addonAmt = (addons,size) => { let t=0; addonGroups.forEach(g=>{ const sel=addons[g.id];if(!sel)return; const ids=g.selectionType==='single'?(sel?[sel]:[]):(sel||[]); ids.forEach(oid=>{const o=(g.options||[]).find(x=>x.id===oid);if(o)t+=getSizePrice(o,size);}); }); return t; };
  const addonNames = (addons) => { const n=[]; addonGroups.forEach(g=>{ const sel=addons[g.id];if(!sel)return; const ids=g.selectionType==='single'?(sel?[sel]:[]):(sel||[]); ids.forEach(oid=>{const o=(g.options||[]).find(x=>x.id===oid);if(o)n.push(optionLabel(o));}); }); return n; };
  const confirm = () => {
    const filled=slots.filter(s=>selections[s.key]?.item);
    if(!filled.length){toast.error('Select free pizza(s)');return;}
    filled.forEach(slot=>{ const{item,addons}=selections[slot.key]; const amt=addonAmt(addons,slot.size);
      onAdd({ menuId:item.id, name:`🎁 ${item.name} (${slot.size}) — FREE`, price:amt, qty:1, addons:addonNames(addons), sizeLabel:slot.size, sizeBasePrice:0, isFree:true });
    });
    onClose();
  };
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div style={{background:'#fff',borderRadius:14,padding:20,width:540,maxHeight:'85vh',overflowY:'auto',boxShadow:'0 8px 30px rgba(0,0,0,.18)'}} onClick={e=>e.stopPropagation()}>
        <div style={{fontWeight:700,fontSize:16,marginBottom:3}}>🎁 Choose Your Free Pizza!</div>
        <div style={{fontSize:13,color:'#888',marginBottom:14}}>You have <b>{slots.length}</b> free pizza{slots.length>1?'s':''} to claim. Add-ons are chargeable.</div>
        {slots.map(slot=>{
          const eligible=pizzaItems.filter(m=>{const override=m.sizePrices?.[slot.size];const p=override!=null&&override!==''?+override:autoSizePrice(m.basePrice,SIZE_MULT[slot.size]||1);return p<=slot.maxPrice;});
          const sel=selections[slot.key];
          return (
            <div key={slot.key} style={{marginBottom:18}}>
              <div style={{fontSize:12,fontWeight:600,color:'#3B6D11',background:'#EAF3DE',borderRadius:6,padding:'6px 10px',marginBottom:9}}>🎁 Free {slot.size} Pizza — choose any at ≤₹{slot.maxPrice}</div>
              <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(130px,1fr))',gap:7,marginBottom:10}}>
                {eligible.map(item=>{
                  const isSel=sel?.item?.id===item.id;
                  return (<div key={item.id} onClick={()=>selectForSlot(slot.key,item)} style={{border:`1.5px solid ${isSel?'#D85A30':'#eee'}`,borderRadius:9,padding:'9px 7px',textAlign:'center',cursor:'pointer',background:isSel?'#FAECE7':'#fff'}}>
                    <div style={{fontSize:20}}>{item.emoji}</div><div style={{fontSize:11,fontWeight:500,marginTop:3,lineHeight:1.3}}>{item.name}</div><div style={{fontSize:11,color:'#3B6D11',marginTop:2}}>FREE 🎁</div>
                  </div>);
                })}
              </div>
              {sel?.item && addonGroups.filter(g=>(g.appliesTo||['pizza']).includes('pizza')).map(g=>{
                const opts=(g.options||[]).filter(o=>o.active!==false); if(!opts.length) return null;
                return (
                  <div key={g.id} style={{padding:'8px 12px',background:'#fafafa',borderRadius:8,marginBottom:6,border:'1px solid #f0f0f0'}}>
                    <div style={{fontSize:11,fontWeight:600,color:'#888',marginBottom:5}}>{g.name} <span style={{color:'#D85A30'}}>(charged on free pizza too)</span></div>
                    <div style={{display:'flex',flexWrap:'wrap',gap:5}}>
                      {opts.map(opt=>{
                        const pr=getSizePrice(opt,slot.size); const selG=sel.addons[g.id]; const active=g.selectionType==='single'?selG===opt.id:(selG||[]).includes(opt.id);
                        return (<div key={opt.id} onClick={()=>toggleAddon(slot.key,g.id,opt.id,g.selectionType)} style={{padding:'3px 9px',borderRadius:20,fontSize:11,cursor:'pointer',border:`1px solid ${active?'#D85A30':'#eee'}`,background:active?'#FAECE7':'#fff',color:active?'#993C1D':'#555'}}>{optionLabel(opt)} {pr>0?`+₹${pr}`:'Free'}</div>);
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })}
        <div style={{display:'flex',gap:8,marginTop:14,paddingTop:14,borderTop:'1px solid #eee'}}>
          <button className="btn" style={{flex:1}} onClick={onClose}>Skip for now</button>
          <button className="btn btn-primary" style={{flex:1.5}} onClick={confirm}>🎁 Add Free Pizza to Bill</button>
        </div>
      </div>
    </div>
  );
}

// ─── TABLE / SLOT GRID ─────────────────────────────────────────
function SlotGrid({ orderType, openOrdersMap, onSelectSlot, onSendStatus }) {
  const labelPrefix = orderType === 'dinein' ? 'T' : '';
  return (
    <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(90px,1fr))', gap:10, padding:6 }}>
      {Array.from({ length: SLOT_COUNT }, (_,i)=>i+1).map(n => {
        const key = `${orderType}_${n}`;
        const open = openOrdersMap[key];
        return (
          <div key={n} onClick={()=>onSelectSlot(n)} style={{
            border: open ? '2px solid #D85A30' : '1.5px solid #eee',
            background: open ? '#FAECE7' : '#fff',
            borderRadius: 10, padding: '14px 8px', textAlign:'center', cursor:'pointer',
            minHeight: 78, display:'flex', flexDirection:'column', justifyContent:'center',
          }}>
            <div style={{ fontSize:20, fontWeight:700, color: open ? '#D85A30' : '#333' }}>{labelPrefix}{n}</div>
            {open && (
              <>
                <div style={{ fontSize:11, color:'#993C1D', marginTop:4 }}>{elapsedLabel(open.startedAt)}</div>
                <div style={{ fontSize:13, fontWeight:700, color:'#D85A30' }}>₹{fmt(open.grandTotal ?? Math.round((open.subtotal||0)*(1+GST_RATE)))}</div>
                {/* WhatsApp status messages: "order ready" on every tab, "delivered" on the delivery tab only */}
                <div style={{ display:'flex', gap:4, justifyContent:'center', flexWrap:'wrap', marginTop:7 }} onClick={e=>e.stopPropagation()}>
                  <button type="button" onClick={()=>onSendStatus(n,'ready')}
                    title={open.customerPhone ? (open.readySentAt ? 'Order-ready message already sent — click to send again' : 'Send the "order ready" WhatsApp message') : 'Add a customer phone number to this order first'}
                    style={{ fontSize:10, padding:'3px 7px', borderRadius:12, cursor:'pointer', border:'1px solid #3B6D11', background:open.readySentAt?'#3B6D11':'#EAF3DE', color:open.readySentAt?'#fff':'#3B6D11', opacity:open.customerPhone?1:.45 }}>
                    {open.readySentAt?'✓ ':''}📲 Ready
                  </button>
                  {orderType==='delivery' && (
                    <button type="button" onClick={()=>onSendStatus(n,'delivered')}
                      title={open.customerPhone ? (open.deliveredSentAt ? 'Delivered message already sent — click to send again' : 'Send the "delivered" WhatsApp message') : 'Add a customer phone number to this order first'}
                      style={{ fontSize:10, padding:'3px 7px', borderRadius:12, cursor:'pointer', border:'1px solid #185FA5', background:open.deliveredSentAt?'#185FA5':'#E6F1FB', color:open.deliveredSentAt?'#fff':'#185FA5', opacity:open.customerPhone?1:.45 }}>
                      {open.deliveredSentAt?'✓ ':''}📲 Delivered
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── MAIN BILLING PAGE ────────────────────────────────────────
export default function Billing() {
  const { user } = useAuth();

  const [menuItems, setMenuItems] = useState([]);
  const [combos, setCombos]       = useState([]);
  const [weeklyOffers, setWeeklyOffers] = useState([]);
  const [addonGroups, setAddonGroups] = useState([]);
  const [recipes, setRecipes]     = useState({});
  const [inventory, setInventory] = useState([]);
  const [settings, setSettings]   = useState({});
  const [openOrdersMap, setOpenOrdersMap] = useState({}); // { "dinein_1": {...} }

  const [orderType, setOrderType] = useState('dinein'); // dinein | takeout | delivery
  const [activeSlot, setActiveSlot] = useState(null);    // null = show grid; number = punching that slot

  const [activeCat, setActiveCat] = useState('');
  const [search, setSearch]       = useState('');
  const [cart, setCart]           = useState([]);
  const [payMode, setPayMode]     = useState('cash');
  const [saving, setSaving]       = useState(false);

  const [discountMode, setDiscountMode] = useState('percent'); // 'percent' | 'fixed'
  const [manualDiscountPercent, setManualDiscountPercent] = useState(0);
  const [manualDiscountFixed, setManualDiscountFixed] = useState(0);

  const [custPhone, setCustPhone]     = useState('');
  const [custName, setCustName]       = useState('');
  const [custEmail, setCustEmail]     = useState('');
  const [custAddress, setCustAddress] = useState('');
  const [custHistory, setCustHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [custLoyaltyPoints, setCustLoyaltyPoints] = useState(0);
  const [custExpiring, setCustExpiring] = useState(null); // { at, pts } — the next batch of points to expire
  const [redeemRs, setRedeemRs] = useState(0);

  const [addItemModal, setAddItemModal] = useState(null);
  const [comboModal, setComboModal]     = useState(null);
  const [upsellModal, setUpsellModal]   = useState(false);
  const [upsellQtys, setUpsellQtys]     = useState({});
  const [printStage, setPrintStage]     = useState('none'); // 'none' → 'kot' → 'bill' — drives the Print KOT/Print Bill/Settle button
  const [provisionalBillNo, setProvisionalBillNo] = useState(null); // generated once at Print Bill, reused by Settle so numbers stay consistent
  const [billActionPopup, setBillActionPopup] = useState(false); // Send Message / Print Bill+Send Message choice
  const [offerModal, setOfferModal]     = useState(false);
  const [freePicker, setFreePicker]     = useState(false);
  const [settleConfirm, setSettleConfirm] = useState(false); // "Are you sure you want to settle?"

  const loadedSlotKey = useRef(null); // tracks which slot's data is currently loaded, to avoid save-on-load loops

  // ── Live data ──
  useEffect(() => {
    const u1 = onSnapshot(collection(db,'menu'), s => {
      const list = s.docs.map(d=>({id:d.id,...d.data()})).filter(i=>i.available!==false);
      setMenuItems(list);
      if (!activeCat && list.length) setActiveCat(list[0].cat);
    });
    const u2 = onSnapshot(collection(db,'combos'), s => setCombos(s.docs.map(d=>({id:d.id,...d.data()})).filter(c=>c.active!==false)));
    const u3 = onSnapshot(collection(db,'weeklyOffers'), s => setWeeklyOffers(s.docs.map(d=>({id:d.id,...d.data()}))));
    const u4 = onSnapshot(collection(db,'addonGroups'), s => setAddonGroups(s.docs.map(d=>({id:d.id,...d.data()})).sort((a,b)=>(a.order||0)-(b.order||0))));
    const u5 = onSnapshot(doc(db,'config','settings'), s => { if(s.exists()) setSettings(s.data()); });
    const u6 = onSnapshot(collection(db,'openOrders'), s => {
      const map = {}; s.docs.forEach(d => { map[d.id] = d.data(); }); setOpenOrdersMap(map);
    });
    getDocs(collection(db,'recipes')).then(s=>{ const r={}; s.forEach(d=>r[d.id]=d.data().ingredients||[]); setRecipes(r); });
    getDocs(collection(db,'inventory')).then(s=>setInventory(s.docs.map(d=>({id:d.id,...d.data()}))));
    return () => { u1();u2();u3();u4();u5();u6(); };
  }, []); // eslint-disable-line

  const expiryDays = expiryDaysFrom(settings); // loyalty points expire this many days after they are earned (0 = never)
  const upsellItems = Array.isArray(settings.upsellItems) ? settings.upsellItems : DEFAULT_UPSELL_ITEMS;
  const activeOffer = weeklyOffers.find(o => o.active !== false && o.dayOfWeek === TODAY_DAY);
  const pizzaItems  = menuItems.filter(m => m.sizes === 'pizza');
  const CATS = useMemo(() => {
    const seen = new Map();
    menuItems.forEach(m => { const k=(m.cat||'').trim().toLowerCase(); if (k && !seen.has(k)) seen.set(k, m.cat.trim()); });
    // admin-defined order first (Admin Panel → Categories), then any other categories found on items
    const ordered = [];
    (settings.menuCategories || []).forEach(name => { const k=String(name).trim().toLowerCase(); if (seen.has(k)) { ordered.push(seen.get(k)); seen.delete(k); } });
    const cats = [...ordered, ...Array.from(seen.values())];
    if (combos.length) cats.push('Combo');
    return cats;
  }, [menuItems, combos, settings.menuCategories]);
  const menuItemsById = useMemo(() => Object.fromEntries(menuItems.map(m=>[m.id,m])), [menuItems]);

  // ── Enter punching mode for a slot: load its open order (if any) ──
  const enterSlot = (n) => {
    const key = `${orderType}_${n}`;
    const existing = openOrdersMap[key];
    loadedSlotKey.current = key;
    isFirstEffectAfterEnter.current = true; // skip the next autosave tick (it's just the load, not a real edit)
    skipAutosave.current = false; // re-arm autosave for this fresh session
    if (existing) {
      setCart((existing.items || []).map(i => (existing.printStage && existing.printStage !== 'none' && i.kotPrinted === undefined) ? { ...i, kotPrinted: true } : i));
      setCustPhone(existing.customerPhone || '');
      setCustName(existing.customerName || '');
      setCustEmail(existing.customerEmail || '');
      setCustAddress(existing.customerAddress || '');
      setDiscountMode(existing.discountMode || 'percent');
      setManualDiscountPercent(existing.manualDiscountPercent || 0);
      setManualDiscountFixed(existing.manualDiscountFixed || 0);
      setRedeemRs(existing.redeemRs || 0);
      setPayMode(existing.paymentMode || 'cash');
      setPrintStage(existing.printStage || 'none');
      setProvisionalBillNo(existing.provisionalBillNo || null);
      if (existing.customerPhone) lookupCustomer(existing.customerPhone, true);
    } else {
      setCart([]); setCustPhone(''); setCustName(''); setCustEmail(''); setCustAddress('');
      setDiscountMode('percent'); setManualDiscountPercent(0); setManualDiscountFixed(0);
      setRedeemRs(0); setPayMode('cash'); setCustLoyaltyPoints(0); setCustExpiring(null);
      setPrintStage('none'); setProvisionalBillNo(null);
    }
    setActiveSlot(n);
  };

  const resetToGrid = () => { setActiveSlot(null); loadedSlotKey.current = null; };

  // Save & Back just saves and leaves — the suggested-item popup now lives on Print KOT instead
  const handleSaveAndBack = async () => {
    if (!cart.length) { resetToGrid(); return; }
    await persistOpenOrder();
    resetToGrid();
  };

  // ── One place for all bill maths. Prices stay exact; GST is exact; only the FINAL total is rounded to whole rupees ──
  const computeBill = (itemsArr, redeem = redeemRs) => {
    const sub = round2(itemsArr.reduce((a,i)=>a+(Number(i.price)||0),0));
    const manualDiscountAmt = discountMode === 'percent' ? round2(sub * manualDiscountPercent / 100) : Math.min(manualDiscountFixed, sub);
    const afterManualDiscount = Math.max(0, round2(sub - manualDiscountAmt));
    const taxable = Math.max(0, round2(afterManualDiscount - redeem));
    const gst  = round2(taxable * GST_RATE);
    const cgst = round2(taxable * GST_RATE / 2);
    const sgst = round2(gst - cgst);
    const total = Math.round(taxable + gst);
    const roundOff = round2(total - (taxable + gst));
    return { sub, manualDiscountAmt, afterManualDiscount, taxable, cgst, sgst, gst, roundOff, total };
  };
  const newUid = () => `${Date.now()}_${Math.random().toString(36).slice(2,7)}`;

  // ── Persist current punching state to the open order doc ──
  const persistOpenOrder = async (overrides = {}) => {
    if (activeSlot == null) return;
    const key = `${orderType}_${activeSlot}`;
    const itemsNow = (overrides.items || cart).map(i => ({ ...i, addons: cleanAddons(i.addons) }));
    const t = computeBill(itemsNow, redeemRs);
    const prevStart = openOrdersMap[key]?.startedAt;
    const payload = cleanForFirestore({
      orderType, slot: activeSlot, items: itemsNow,
      customerPhone: custPhone||null, customerName: custName||null, customerEmail: custEmail||null, customerAddress: custAddress||null,
      discountMode, manualDiscountPercent, manualDiscountFixed, redeemRs, paymentMode: payMode,
      printStage, provisionalBillNo: provisionalBillNo || null,
      subtotal: t.sub, grandTotal: t.total, // grandTotal = final amount after GST, shown on the table tile
      startedAt: prevStart?.toDate ? prevStart : serverTimestamp(),
      updatedAt: serverTimestamp(),
      ...overrides,
    });
    await setDoc(doc(db,'openOrders',key), payload, { merge: true });
  };

  // Auto-save whenever cart/discount/customer fields change WHILE punching (skip the initial load tick)
  const isFirstEffectAfterEnter = useRef(true);
  const skipAutosave = useRef(false); // set true once a bill is settled, so clearing local state afterward can't recreate the deleted openOrders doc
  useEffect(() => {
    if (activeSlot == null) return;
    if (skipAutosave.current) return;
    if (isFirstEffectAfterEnter.current) { isFirstEffectAfterEnter.current = false; return; }
    persistOpenOrder();
    // eslint-disable-next-line
  }, [cart, custPhone, custName, custEmail, custAddress, discountMode, manualDiscountPercent, manualDiscountFixed, redeemRs, payMode]);

  const lookupCustomer = async(phone, silent)=>{
    if(!phone||phone.length<10) return;
    const cq=query(collection(db,'customers'),where('phone','==',phone));
    const cSnap=await getDocs(cq);
    if(!cSnap.empty){ const data=cSnap.docs[0].data(); if(!silent){setCustName(data.name||''); setCustEmail(data.email||''); setCustAddress(data.address||''); toast.success(`Welcome back, ${data.name}! 👋`);} }
    const lSnap = await getDoc(doc(db,'loyalty',phone));
    // only points that have not expired can be used
    const lots = lotsFromDoc(lSnap.exists() ? lSnap.data() : null);
    setCustLoyaltyPoints(balanceOf(activeLots(lots, expiryDays)));
    setCustExpiring(nextExpiry(lots, expiryDays));
    const hq=query(collection(db,'bills'),where('customerPhone','==',phone),orderBy('createdAt','desc'));
    const hSnap=await getDocs(hq);
    if(!hSnap.empty){setCustHistory(hSnap.docs.map(d=>({id:d.id,...d.data()})).slice(0,5)); if(!silent) setShowHistory(true);}
  };

  const getSatEntitlements = ()=>{
    if(!activeOffer||activeOffer.type!=='buy1get1') return [];
    const ents=[];
    (activeOffer.rules||[]).forEach(rule=>{
      const qualifying=cart.filter(c=>c.sizeLabel===rule.size&&!c.isFree&&(c.sizeBasePrice||0)>=rule.minPrice);
      const free=cart.filter(c=>c.sizeLabel===rule.size&&c.isFree);
      const owed=qualifying.length-free.length;
      if(owed>0){ const maxPrice=Math.max(...qualifying.map(c=>c.sizeBasePrice||0)); ents.push({size:rule.size,count:owed,maxPrice,minPrice:rule.minPrice}); }
    });
    return ents;
  };

  const addToCart = (item) => {
    setCart(p => [...p, { ...item, addons: cleanAddons(item.addons), uid: newUid(), kotPrinted: false }]);
    setPrintStage('none');
  };

  // Lines already sent to the kitchen (KOT printed) can never be removed
  const removeFromCart = (uid, silent = false) => {
    const target = cart.find(i => i.uid === uid);
    if (target?.kotPrinted) {
      if (!silent) toast.error("Already sent to the kitchen (KOT printed) — can't be removed.");
      return false;
    }
    setCart(prev => {
      const removed = prev.find(i => i.uid === uid);
      let next = prev.filter(i => i.uid !== uid);
      // Buy1Get1 offers (e.g. YOYO Saturday): removing a qualifying paid pizza can leave an
      // already-earned free pizza with nothing to justify it — drop the extra free one(s) too.
      if (removed && !removed.isFree && activeOffer?.type === 'buy1get1') {
        (activeOffer.rules || []).forEach(rule => {
          if (removed.sizeLabel !== rule.size) return;
          let qualifying = next.filter(c => c.sizeLabel === rule.size && !c.isFree && (c.sizeBasePrice||0) >= rule.minPrice).length;
          let free = next.filter(c => c.sizeLabel === rule.size && c.isFree && !c.kotPrinted);
          let excess = next.filter(c => c.sizeLabel === rule.size && c.isFree).length - qualifying;
          while (excess > 0 && free.length) {
            const last = free[free.length - 1];
            next = next.filter(c => c.uid !== last.uid);
            free = free.slice(0, -1);
            excess--;
          }
        });
      }
      return next;
    });
    return true;
  };

  // Identical lines are shown as one row with a quantity box (each unit is still its own line underneath)
  const cartGroups = (() => {
    const m = new Map();
    cart.forEach(i => {
      const k = i.isFree ? 'free_' + i.uid : [i.menuId||'', i.name, i.price, (i.addons||[]).join(','), i.offerApplied?1:0, i.isCombo?1:0, i.sizeLabel||'', i.kotPrinted?1:0].join('|');
      if (!m.has(k)) m.set(k, { key: k, items: [] });
      m.get(k).items.push(i);
    });
    return [...m.values()];
  })();

  const setGroupQty = (group, raw) => {
    const n = Math.max(0, Math.floor(Number(raw) || 0));
    const cur = group.items.length;
    if (n === cur) return;
    const sample = group.items[0];
    if (n > cur) {
      const extra = Array.from({ length: n - cur }, () => ({ ...sample, uid: newUid(), kotPrinted: false }));
      setCart(p => [...p, ...extra]); setPrintStage('none');
      return;
    }
    if (sample.kotPrinted) { toast.error("Already sent to the kitchen (KOT printed) — can't be reduced."); return; }
    group.items.slice(n).forEach(it => removeFromCart(it.uid, true));
  };
  const removeGroup = (group) => {
    if (group.items[0].kotPrinted) { toast.error("Already sent to the kitchen (KOT printed) — can't be removed."); return; }
    group.items.forEach(it => removeFromCart(it.uid, true));
  };

  const { sub, manualDiscountAmt, afterManualDiscount } = computeBill(cart, 0);

  const offerActiveInCart = cart.some(i => i.offerApplied || i.isFree || i.isCombo) || manualDiscountAmt > 0;
  const capPercent = offerActiveInCart ? LOYALTY_CAP_W_OFFER : LOYALTY_CAP_NORMAL;
  const meetsMinOrder = afterManualDiscount >= LOYALTY_MIN_ORDER;
  const maxRedeemableRs = meetsMinOrder
    ? Math.max(0, Math.min(Math.floor(afterManualDiscount * capPercent), Math.floor(custLoyaltyPoints / POINTS_PER_RUPEE_REDEEM)))
    : 0;
  useEffect(() => { if (redeemRs > maxRedeemableRs) setRedeemRs(maxRedeemableRs); }, [maxRedeemableRs]); // eslint-disable-line

  const { cgst, sgst, roundOff, total } = computeBill(cart, redeemRs);

  const satEntitlements = getSatEntitlements();
  const orderLabel = orderType === 'dinein' ? `T${activeSlot}` : (orderType === 'takeout' ? `Takeaway #${activeSlot}` : `Delivery #${activeSlot}`);

  // ── Stage 1: Print KOT — suggested-item popup first, then ONLY the new items go to the kitchen ──
  const handlePrintKOTClick = () => {
    if (!cart.length) { toast.error('Add items first'); return; }
    if (!cart.some(i => !i.kotPrinted)) {
      setPrintStage('kot'); persistOpenOrder({ printStage: 'kot' });
      toast('All items were already sent to the kitchen');
      return;
    }
    if (!upsellItems.length) { finishPrintKOT(false); return; } // no suggested items configured → print straight away
    setUpsellModal(true); setUpsellQtys({});
  };

  const finishPrintKOT = async (withUpsell = false) => {
    setUpsellModal(false);
    let finalCart = cart;
    if (withUpsell) {
      const additions = [];
      Object.entries(upsellQtys).forEach(([id,qty])=>{
        if(qty>0){ const u=upsellItems.find(x=>x.id===id); if(u) for(let i=0;i<qty;i++) additions.push({uid:newUid(),menuId:u.id,name:u.name,price:Number(u.price)||0,addons:[],isFree:false,kotPrinted:false}); }
      });
      if (additions.length) finalCart = [...cart, ...additions];
    }
    const toPrint = finalCart.filter(i => !i.kotPrinted);          // only items the kitchen hasn't seen yet
    const isAddition = finalCart.some(i => i.kotPrinted);          // header says "ADDITIONAL ITEMS" when some were sent before
    printKOT({
      billNo: orderLabel, tableLabel: orderType==='dinein'?'Dine In':(orderType==='takeout'?'Takeaway':'Delivery'),
      customerName: custName, items: toPrint, isAddition, createdAt: { toDate: () => new Date() },
    });
    const marked = finalCart.map(i => i.kotPrinted ? i : { ...i, kotPrinted: true });
    setCart(marked);
    setPrintStage('kot');
    await persistOpenOrder({ items: marked, printStage: 'kot' });
  };

  // ── Stage 2: Print Bill — choose Send Message only, or Print (×2) + Send Message ──
  const handlePrintBillClick = () => {
    if (!cart.length) { toast.error('Add items first'); return; }
    setBillActionPopup(true);
  };

  // mode 'print' = print 2 copies of the bill, 'none' = do nothing (go straight on to Settle)
  const finishPrintBillAction = async (mode) => {
    setBillActionPopup(false);
    let billNo = provisionalBillNo;
    if (mode === 'print') {
      if (!billNo) { billNo = await getNextBillNumber(); setProvisionalBillNo(billNo); }
      const draftBill = {
        billNo, orderLabel, items: cart,
        subtotal: sub, manualDiscountPercent, manualDiscountAmt,
        cgst, sgst, roundOff, loyaltyDiscount: redeemRs, pointsRedeemed: redeemRs * POINTS_PER_RUPEE_REDEEM,
        total, paymentMode: payMode,
        customerName: custName || null, customerPhone: custPhone || null,
        offer: activeOffer?.name || null,
        createdAt: { toDate: () => new Date() },
      };
      // two copies = two print jobs, so the thermal printer cuts after each bill
      printReceipt({ bill: draftBill, settings, loyaltyData: null, copies: 2 });
    }
    setPrintStage('bill');
    await persistOpenOrder({ printStage: 'bill', ...(billNo ? { provisionalBillNo: billNo } : {}) });
  };

  const handleSettle = () => { if(!cart.length){toast.error('Add items first');return;} setSettleConfirm(true); };

  const commitOrder = async () => {
    setSaving(true);
    try {
      let finalCart = [...cart];
      const T0 = computeBill(finalCart, 0);
      const finalRedeem = Math.min(redeemRs, Math.floor(custLoyaltyPoints / POINTS_PER_RUPEE_REDEEM), T0.afterManualDiscount);
      const T = computeBill(finalCart, finalRedeem);
      const finalSub = T.sub, finalManualDiscAmt = T.manualDiscountAmt;
      const finalCgst = T.cgst, finalSgst = T.sgst, finalGst = T.gst;
      const finalRoundOff = T.roundOff, finalTotal = T.total;

      const matAgg={};
      finalCart.forEach(ci=>(recipes[ci.menuId]||[]).forEach(r=>{
        const m=inventory.find(x=>x.id===r.matId);if(!m)return;
        if(!matAgg[r.matId])matAgg[r.matId]={mat:m,total:0}; matAgg[r.matId].total+=r.qty;
      }));
      const deducted=[];
      for(const{mat,total:qty}of Object.values(matAgg)){
        await updateDoc(doc(db,'inventory',mat.id),{stock:Math.max(0,+(mat.stock-qty).toFixed(4))});
        deducted.push({name:mat.name||'Item',unit:mat.unit||'pcs',qty:qty||0});
      }

      const billNo = provisionalBillNo || await getNextBillNumber();

      if (custPhone) {
        const cq = query(collection(db,'customers'), where('phone','==',custPhone));
        const cSn = await getDocs(cq);
        if (cSn.empty) await addDoc(collection(db,'customers'),{phone:custPhone,name:custName,email:custEmail,address:custAddress,createdAt:serverTimestamp()});
        else await updateDoc(cSn.docs[0].ref,{name:custName,email:custEmail,address:custAddress});
      }

      // loyalty points are earned on the amount before GST and after discounts (what the customer actually pays, excluding tax)
      const earnedPts = custPhone ? Math.floor(T.taxable) : 0;

      const billDataCore = cleanForFirestore({
        billNo, orderType, table: orderType==='dinein'?`T${activeSlot}`:orderType, orderLabel,
        channel:'dinein',
        items: finalCart.map(i=>({ name:i.name||'Item', price:typeof i.price==='number'?i.price:0, addons:cleanAddons(i.addons), isFree:!!i.isFree })),
        subtotal: finalSub, discountMode, manualDiscountPercent, manualDiscountFixed, manualDiscountAmt: finalManualDiscAmt,
        cgst: finalCgst, sgst: finalSgst, gst: finalGst, roundOff: finalRoundOff,
        loyaltyDiscount: finalRedeem, pointsRedeemed: finalRedeem * POINTS_PER_RUPEE_REDEEM, pointsEarned: earnedPts,
        total: finalTotal, paymentMode: payMode,
        materialsDeducted: deducted, status:'Preparing',
        customerPhone: custPhone||null, customerName: custName||null,
        offer: activeOffer?.name || null,
        createdBy: user.uid,
      });
      const billData = { ...billDataCore, createdAt: serverTimestamp() };
      await addDoc(collection(db,'bills'), billData);

      let ptsEarned = 0, newTotalPts = 0;
      if (custPhone) {
        ptsEarned = earnedPts;
        const redeemedPoints = finalRedeem * POINTS_PER_RUPEE_REDEEM;
        const lSnap = await getDoc(doc(db,'loyalty',custPhone));
        const ld = lSnap.exists() ? lSnap.data() : null;
        const allLots = lotsFromDoc(ld);                              // older balance without dates counts as earned now
        const liveLots = activeLots(allLots, expiryDays);             // points that have not expired
        const expiredNow = balanceOf(allLots) - balanceOf(liveLots);
        const afterRedeem = redeemFifo(liveLots, redeemedPoints);     // oldest points are used first
        const newLots = compactLots(ptsEarned > 0 ? [...afterRedeem, { pts: ptsEarned, at: Date.now() }] : afterRedeem);
        newTotalPts = balanceOf(newLots);
        await setDoc(doc(db,'loyalty',custPhone), {
          name: custName || (ld ? ld.name : 'Customer'),
          phone: custPhone, points: newTotalPts, lots: newLots, lastEarnedAt: Date.now(),
          pointsExpired: (ld?.pointsExpired || 0) + expiredNow,
          totalVisits: (ld ? (ld.totalVisits||0) : 0) + 1,
          ...(lSnap.exists() ? {} : { createdAt: serverTimestamp() }),
        }, { merge: true });
      }

      // Free up this table/slot — deleting closes it out
      await deleteDoc(doc(db,'openOrders',`${orderType}_${activeSlot}`));
      skipAutosave.current = true; // prevent the effect below (triggered by setCart([]) etc.) from recreating the doc we just deleted

      toast.success(`Bill ${billNo} settled! 🎉`);
      setCart([]);
      setPrintStage('none'); setProvisionalBillNo(null);
      resetToGrid();   // table is free again — back to the table list
      getDocs(collection(db,'inventory')).then(s=>setInventory(s.docs.map(d=>({id:d.id,...d.data()}))));
    } catch(err) { toast.error(err.message); }
    setSaving(false);
  };

  // ── WhatsApp status messages from the table tiles ──
  const sendStatusMessage = async (n, kind) => {
    const key = `${orderType}_${n}`;
    const open = openOrdersMap[key];
    if (!open) return;
    if (!open.customerPhone) { toast.error('This order has no customer phone number. Open it and add one first.'); return; }
    const sentField = kind === 'ready' ? 'readySentAt' : 'deliveredSentAt';
    if (open[sentField] && !window.confirm('This message was already sent. Send it again?')) return;
    const label = orderType==='dinein' ? `Table T${n}` : orderType==='takeout' ? 'Takeout Order' : 'Home Delivery';
    const message = kind === 'ready'
      ? (orderType==='dinein' ? 'Your order is ready and will be served at your table shortly! 🍕'
        : orderType==='takeout' ? 'Your order is ready. Please collect it from the counter! 🍕'
        : 'Your order is ready and our delivery partner will leave shortly! 🛵')
      : 'Your order has been delivered. Enjoy your meal! 🍕 Thank you for choosing VIP Pizza.';
    const itemsText = (open.items || []).map(i => i.name).join(', ');
    const r = await sendOrderReadyWhatsApp({
      phone: open.customerPhone, name: open.customerName || 'Customer',
      billNo: open.provisionalBillNo || (orderType==='dinein' ? `T${n}` : `${orderType==='takeout'?'Takeaway':'Delivery'} #${n}`),
      tableInfo: label, itemsText, total: fmt(open.grandTotal ?? Math.round((open.subtotal||0)*(1+GST_RATE))), message,
    });
    if (r.ok) {
      toast.success(kind === 'ready' ? 'Order-ready message sent' : 'Delivered message sent');
      try { await setDoc(doc(db,'openOrders',key), { [sentField]: Date.now() }, { merge: true }); } catch (e) { /* the message was sent; only the tick mark failed */ }
    } else toast.error(r.error, { duration: 8000 });
  };

  const filteredTiles = useMemo(() => {
    if (search.trim()) {
      const s = search.toLowerCase();
      const itemTiles = menuItems.filter(m => (m.name||'').toLowerCase().includes(s)).map(m => ({ type:'item', data:m }));
      const comboTiles = combos.filter(c => (c.name||'').toLowerCase().includes(s)).map(c => ({ type:'combo', data:c }));
      return [...itemTiles, ...comboTiles];
    }
    if (activeCat === 'Combo') return combos.map(c => ({ type:'combo', data:c }));
    return menuItems.filter(m => (m.cat||'').trim().toLowerCase() === (activeCat||'').trim().toLowerCase()).map(m => ({ type:'item', data:m }));
  }, [search, activeCat, menuItems, combos]);

  // ══════════════════ RENDER ══════════════════

  // ── GRID VIEW ──
  if (activeSlot == null) {
    return (
      <div>
        <div style={{display:'flex',gap:8,marginBottom:16}}>
          {[['dinein','🪑 Dine In'],['takeout','🥡 Takeaway'],['delivery','🛵 Delivery']].map(([v,l])=>(
            <button key={v} onClick={()=>setOrderType(v)} style={{padding:'10px 24px',borderRadius:10,fontSize:14,cursor:'pointer',border:'2px solid',
              borderColor:orderType===v?'#D85A30':'#eee',background:orderType===v?'#D85A30':'#fff',color:orderType===v?'#fff':'#666',fontWeight:600}}>{l}</button>
          ))}
        </div>
        <div className="card">
          <div className="sec-title">{orderType==='dinein'?'Select Table':orderType==='takeout'?'Select Takeaway Slot':'Select Delivery Slot'}</div>
          <SlotGrid orderType={orderType} openOrdersMap={openOrdersMap} onSelectSlot={enterSlot} onSendStatus={sendStatusMessage} />
        </div>
      </div>
    );
  }

  // ── PUNCHING VIEW ──
  return (
    <div style={{display:'flex',gap:14}}>
      <div className="card" style={{flex:1.4,padding:12,display:'flex',flexDirection:'column',gap:9}}>
        <div style={{display:'flex',alignItems:'center',gap:10}}>
          <button className="btn" onClick={resetToGrid} style={{ fontSize:13 }}>← Tables</button>
          <div style={{ fontWeight:700, fontSize:15 }}>{orderLabel}</div>
        </div>
        <input className="input" placeholder="🔍 Search all items…" value={search} onChange={e=>setSearch(e.target.value)} />
        {activeOffer && !search && (
          <div style={{background:'linear-gradient(135deg,#D85A30,#EF9F27)',borderRadius:9,padding:'10px 14px',color:'#fff'}}>
            <div style={{fontWeight:700,fontSize:13,marginBottom:3}}>🔥 {activeOffer.name}</div>
            <div style={{fontSize:12,marginBottom:8,opacity:.9}}>{activeOffer.description}</div>
            {activeOffer.type==='fixed_price' && (
              <button onClick={()=>setOfferModal(true)} style={{background:'rgba(255,255,255,.25)',border:'1.5px solid rgba(255,255,255,.6)',borderRadius:7,color:'#fff',fontWeight:600,fontSize:13,padding:'6px 16px',cursor:'pointer'}}>🎁 Use {activeOffer.name.split(' ').slice(0,2).join(' ')} Offer</button>
            )}
            {activeOffer.type==='buy1get1' && satEntitlements.length>0 && (
              <button onClick={()=>setFreePicker(true)} style={{background:'rgba(255,255,255,.25)',border:'1.5px solid rgba(255,255,255,.6)',borderRadius:7,color:'#fff',fontWeight:600,fontSize:13,padding:'6px 16px',cursor:'pointer'}}>🎁 Claim {satEntitlements.reduce((a,e)=>a+e.count,0)} Free Pizza!</button>
            )}
            {activeOffer.type==='buy1get1' && satEntitlements.length===0 && <div style={{fontSize:11,opacity:.8}}>Add qualifying pizzas to bill to unlock free pizza →</div>}
          </div>
        )}
        {!search && (
          <div style={{display:'flex',flexWrap:'wrap',gap:5}}>
            {CATS.map(c=>(
              <button key={c} onClick={()=>setActiveCat(c)} style={{padding:'3px 9px',borderRadius:20,fontSize:11,cursor:'pointer',border:'1px solid',
                borderColor:c===activeCat?'#D85A30':'#eee',background:c===activeCat?'#D85A30':'#fff',color:c===activeCat?'#fff':'#666'}}>{c}</button>
            ))}
          </div>
        )}
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(110px,1fr))',gap:7,overflowY:'auto',flex:1}}>
          {filteredTiles.map(tile => {
            const it = tile.data;
            return (
              <div key={tile.type+'_'+it.id} onClick={()=>tile.type==='combo'?setComboModal(it):setAddItemModal(it)}
                style={{border:tile.type==='combo'?'1.5px solid #EF9F27':'1px solid #eee',borderRadius:9,padding:'9px 7px',textAlign:'center',cursor:'pointer',background:'#fff'}}
                onMouseEnter={e=>e.currentTarget.style.borderColor='#D85A30'} onMouseLeave={e=>e.currentTarget.style.borderColor=tile.type==='combo'?'#EF9F27':'#eee'}>
                <div style={{fontSize:20}}>{tile.type==='combo'?'🍱':it.emoji}</div>
                <div style={{fontSize:11,fontWeight:500,marginTop:3}}>{it.name}</div>
                <div style={{fontSize:11,color:'#D85A30',marginTop:2}}>{tile.type==='combo'?`₹${it.price}`:`from ₹${it.basePrice}`}</div>
                {search && <div style={{fontSize:9,color:'#aaa',marginTop:1}}>{tile.type==='combo'?'Combo':it.cat}</div>}
              </div>
            );
          })}
          {!filteredTiles.length && <div style={{gridColumn:'1/-1',textAlign:'center',color:'#bbb',padding:30}}>No items found</div>}
        </div>
      </div>

      <div className="card" style={{flex:1,display:'flex',flexDirection:'column',padding:14,maxWidth:340}}>
        <div style={{background:'#f8f8f8',borderRadius:8,padding:10,marginBottom:10}}>
          <div style={{fontSize:11,fontWeight:600,color:'#888',textTransform:'uppercase',marginBottom:7}}>Customer Details (optional)</div>
          <input className="input" placeholder="📞 Phone number" value={custPhone} onChange={e=>setCustPhone(e.target.value)} onBlur={e=>lookupCustomer(e.target.value)} style={{marginBottom:6,fontSize:12}} />
          <input className="input" placeholder="👤 Customer name" value={custName} onChange={e=>setCustName(e.target.value)} style={{marginBottom:6,fontSize:12}} />
          <input className="input" placeholder="✉️ Email (optional)" value={custEmail} onChange={e=>setCustEmail(e.target.value)} style={{marginBottom:6,fontSize:12}} />
          <input className="input" placeholder="📍 Address (for delivery)" value={custAddress} onChange={e=>setCustAddress(e.target.value)} style={{fontSize:12}} />

          {custPhone && custLoyaltyPoints > 0 && (
            <div style={{ marginTop:8, padding:9, background:'#fff', border:'1px solid #EF9F27', borderRadius:7 }}>
              <div style={{ fontSize:12, fontWeight:600, color:'#854F0B', marginBottom:5 }}>🎁 Redeem Loyalty Points</div>
              <div style={{ fontSize:11, color:'#888', marginBottom:6 }}>Available: {custLoyaltyPoints} pts (₹{Math.floor(custLoyaltyPoints/10)} value)</div>
              {custExpiring && <div style={{ fontSize:11, color:'#B3702A', marginBottom:6 }}>⏳ {custExpiring.pts} pts expire on {fmtDate(custExpiring.at)}</div>}
              {!meetsMinOrder ? (
                <div style={{ fontSize:11, color:'#A32D2D' }}>Minimum ₹{LOYALTY_MIN_ORDER} order required to redeem</div>
              ) : maxRedeemableRs === 0 ? (
                <div style={{ fontSize:11, color:'#A32D2D' }}>No points available to redeem</div>
              ) : (
                <>
                  <div style={{ display:'flex', gap:6, alignItems:'center' }}>
                    <input type="number" min="0" max={maxRedeemableRs} value={redeemRs}
                      onChange={e=>setRedeemRs(Math.max(0, Math.min(maxRedeemableRs, +e.target.value || 0)))}
                      style={{ width:70, border:'1px solid #ddd', borderRadius:6, padding:'4px 7px', fontSize:12 }} />
                    <button className="btn" style={{ fontSize:11, padding:'4px 9px' }} onClick={()=>setRedeemRs(maxRedeemableRs)}>Max ₹{maxRedeemableRs}</button>
                  </div>
                  <div style={{ fontSize:10, color:'#aaa', marginTop:4 }}>Cap: {Math.round(capPercent*100)}% of order{offerActiveInCart ? ' (offer/combo/discount active — reduced cap)' : ''}</div>
                </>
              )}
            </div>
          )}
          {custPhone&&custName&&<div style={{marginTop:7,padding:'6px 10px',background:'#FAEEDA',borderRadius:6,fontSize:12,color:'#854F0B'}}>⭐ Loyalty points will be earned{expiryDays>0?` (valid ${expiryDays} days)`:''}</div>}
          {showHistory&&custHistory.length>0&&(
            <div style={{background:'#fff',border:'1px solid #eee',borderRadius:7,padding:9,marginTop:7}}>
              <div style={{fontSize:11,fontWeight:600,color:'#D85A30',marginBottom:6}}>📋 Previous orders ({custHistory.length})</div>
              {custHistory.map(h=>(<div key={h.id} style={{fontSize:11,color:'#666',padding:'4px 0',borderBottom:'1px solid #f5f5f5'}}><b>{h.billNo}</b> · ₹{h.total} · {(h.items||[]).map(i=>i.name).join(', ').slice(0,40)}…</div>))}
              <div style={{fontSize:11,color:'#D85A30',marginTop:5,cursor:'pointer'}} onClick={()=>setShowHistory(false)}>Hide ▲</div>
            </div>
          )}
        </div>

        <div style={{flex:1,overflowY:'auto',minHeight:100,maxHeight:170,marginBottom:8}}>
          {!cart.length && <div style={{color:'#bbb',fontSize:13}}>Tap menu items to punch order</div>}
          {cartGroups.map(g => {
            const i = g.items[0]; const qty = g.items.length;
            const lineTotal = round2(g.items.reduce((acc,x)=>acc+(Number(x.price)||0),0));
            return (
              <div key={g.key} style={{display:'flex',alignItems:'flex-start',gap:6,padding:'5px 0',borderBottom:'1px solid #f5f5f5',fontSize:13}}>
                {i.isFree
                  ? <div style={{width:92,flexShrink:0}} />
                  : <QtyBox value={qty} onCommit={n=>setGroupQty(g,n)} />}
                <div style={{flex:1,minWidth:0}}>
                  <div style={{display:'flex',gap:4,alignItems:'center',flexWrap:'wrap'}}>
                    <span>{i.name}</span>
                    {i.offerApplied&&<span style={{fontSize:9,background:'#EF9F27',color:'#fff',borderRadius:3,padding:'1px 4px'}}>OFFER</span>}
                    {i.isFree&&<span style={{fontSize:9,background:'#3B6D11',color:'#fff',borderRadius:3,padding:'1px 4px'}}>FREE</span>}
                    {i.isCombo&&<span style={{fontSize:9,background:'#7B3FE4',color:'#fff',borderRadius:3,padding:'1px 4px'}}>COMBO</span>}
                    {i.kotPrinted&&<span style={{fontSize:9,background:'#1D9E75',color:'#fff',borderRadius:3,padding:'1px 4px'}}>KOT ✓</span>}
                  </div>
                  {(i.addons||[]).length>0&&<div style={{fontSize:11,color:'#aaa'}}>+{i.addons.join(', ')}</div>}
                </div>
                <span style={{fontWeight:500,color:i.isFree?'#3B6D11':'#1a1a1a',whiteSpace:'nowrap'}}>{i.isFree&&lineTotal===0?'₹0 🎁':`₹${fmt(lineTotal)}`}</span>
                <span onClick={()=>removeGroup(g)} title={i.kotPrinted?'Already sent to the kitchen':'Remove'} style={{cursor:i.kotPrinted?'not-allowed':'pointer',color:i.kotPrinted?'#e0a8a0':'#c0392b',fontSize:20,fontWeight:700,lineHeight:1,padding:'0 2px'}}>×</span>
              </div>
            );
          })}
        </div>

        {/* Manual discount — percent or fixed */}
        <div style={{ marginBottom:8 }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:5 }}>
            <div style={{ fontSize:11, fontWeight:600, color:'#888', textTransform:'uppercase' }}>Manual Discount</div>
            <div style={{ display:'flex', gap:4 }}>
              <button onClick={()=>setDiscountMode('percent')} style={{ fontSize:10, padding:'2px 8px', borderRadius:12, border:'1px solid', borderColor:discountMode==='percent'?'#D85A30':'#ddd', background:discountMode==='percent'?'#FAECE7':'#fff', color:discountMode==='percent'?'#D85A30':'#888', cursor:'pointer' }}>% Off</button>
              <button onClick={()=>setDiscountMode('fixed')} style={{ fontSize:10, padding:'2px 8px', borderRadius:12, border:'1px solid', borderColor:discountMode==='fixed'?'#D85A30':'#ddd', background:discountMode==='fixed'?'#FAECE7':'#fff', color:discountMode==='fixed'?'#D85A30':'#888', cursor:'pointer' }}>₹ Off</button>
            </div>
          </div>
          {discountMode === 'percent' ? (
            <div style={{ display:'flex', gap:5 }}>
              {[0,10,15,20].map(pct=>(
                <button key={pct} onClick={()=>setManualDiscountPercent(pct)} style={{
                  flex:1, padding:'5px 0', borderRadius:6, fontSize:12, cursor:'pointer', border:'1.5px solid',
                  borderColor: manualDiscountPercent===pct ? '#D85A30' : '#eee',
                  background: manualDiscountPercent===pct ? '#FAECE7' : '#fff',
                  color: manualDiscountPercent===pct ? '#D85A30' : '#888', fontWeight: manualDiscountPercent===pct?600:400,
                }}>{pct===0?'None':pct+'%'}</button>
              ))}
            </div>
          ) : (
            <input type="number" min="0" placeholder="Fixed discount amount ₹" value={manualDiscountFixed||''}
              onChange={e=>setManualDiscountFixed(Math.max(0,+e.target.value||0))}
              style={{ width:'100%', border:'1px solid #ddd', borderRadius:6, padding:'6px 9px', fontSize:12 }} />
          )}
        </div>

        <div className="divider" />
        <div style={{fontSize:13,display:'flex',flexDirection:'column',gap:4}}>
          {[
            ['Subtotal','₹'+fmt(sub)],
            ...(manualDiscountAmt>0?[[discountMode==='percent'?`Discount ${manualDiscountPercent}%`:'Discount (fixed)','–₹'+fmt(manualDiscountAmt)]]:[]),
            ...(redeemRs>0?[['Points Redeemed','–₹'+fmt(redeemRs)]]:[]),
            ['CGST @ 2.5%','₹'+fmt(cgst)],
            ['SGST @ 2.5%','₹'+fmt(sgst)],
            ...(Math.abs(roundOff)>=0.005?[['Round off',(roundOff<0?'–':'+')+'₹'+fmt(Math.abs(roundOff))]]:[]),
          ].map(([l,v])=>(<div key={l} style={{display:'flex',justifyContent:'space-between'}}><span style={{color:'#888'}}>{l}</span><span>{v}</span></div>))}
          <div className="divider" style={{margin:'4px 0'}} />
          <div style={{display:'flex',justifyContent:'space-between',fontWeight:700,fontSize:16}}><span>Total</span><span style={{color:'#D85A30'}}>₹{total}</span></div>
        </div>

        <div style={{display:'flex',gap:6,marginTop:10}}>
          {[['cash','💵 Cash'],['online','📲 Online']].map(([v,l])=>(
            <button key={v} onClick={()=>setPayMode(v)} style={{flex:1,padding:'7px 0',borderRadius:7,fontSize:13,cursor:'pointer',border:'1.5px solid',
              borderColor:payMode===v?'#1D9E75':'#eee',background:payMode===v?'#EAF3DE':'#fff',color:payMode===v?'#1D9E75':'#888',fontWeight:payMode===v?600:400}}>{l}</button>
          ))}
        </div>
        <div style={{display:'flex',gap:8,marginTop:10}}>
          <button className="btn" onClick={handleSaveAndBack} style={{flex:1}}>💾 Save & Back</button>
          {printStage === 'none' && (
            <button className="btn btn-primary" onClick={handlePrintKOTClick} style={{flex:2}}>🖨️ Print KOT</button>
          )}
          {printStage === 'kot' && (
            <button className="btn btn-primary" onClick={handlePrintBillClick} style={{flex:2}}>🧾 Print Bill</button>
          )}
          {printStage === 'bill' && (
            <button className="btn btn-primary" onClick={handleSettle} disabled={saving} style={{flex:2}}>{saving?'Settling…':'✅ Settle'}</button>
          )}
        </div>
        <div style={{ fontSize:10, color:'#aaa', textAlign:'center', marginTop:6 }}>
          {cart.some(i=>i.kotPrinted) ? "Items marked KOT ✓ were sent to the kitchen and can't be removed" : 'Items auto-save as you punch — safe to leave and come back to this table'}
        </div>
      </div>

      {addItemModal && <AddItemModal item={addItemModal} onClose={()=>setAddItemModal(null)} onAdd={addToCart} />}
      {comboModal && <ComboBuilderModal combo={comboModal} menuItemsById={menuItemsById} addonGroups={addonGroups} onClose={()=>setComboModal(null)} onAdd={addToCart} />}
      {offerModal && activeOffer && activeOffer.type==='fixed_price' && (
        <OfferPizzaModal offer={activeOffer} addonGroups={addonGroups} pizzaItems={pizzaItems} onClose={()=>setOfferModal(false)} onAdd={addToCart} />
      )}
      {freePicker && satEntitlements.length>0 && (
        <FreePizzaPickerModal entitlements={satEntitlements} addonGroups={addonGroups} pizzaItems={pizzaItems} onClose={()=>setFreePicker(false)} onAdd={addToCart} />
      )}

      {upsellModal && (
        <div className="modal-overlay">
          <div style={{background:'#fff',borderRadius:14,padding:22,width:420,boxShadow:'0 8px 30px rgba(0,0,0,.15)'}}>
            <div style={{fontWeight:700,fontSize:16,marginBottom:4}}>🎯 Before you print…</div>
            <div style={{fontSize:13,color:'#888',marginBottom:14}}>Suggest these to the customer!</div>
            {upsellItems.map(u=>(
              <div key={u.id} style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'10px 12px',border:'1px solid #eee',borderRadius:9,marginBottom:8}}>
                <div><span style={{fontSize:18,marginRight:8}}>{u.emoji}</span><span style={{fontWeight:500}}>{u.name}</span><span style={{color:'#D85A30',marginLeft:8}}>₹{u.price}</span></div>
                <div style={{display:'flex',alignItems:'center',gap:8}}>
                  <button onClick={()=>setUpsellQtys(p=>({...p,[u.id]:Math.max(0,(p[u.id]||0)-1)}))} style={{width:28,height:28,borderRadius:6,border:'1px solid #eee',background:'#f8f8f8',cursor:'pointer',fontSize:16}}>−</button>
                  <span style={{width:24,textAlign:'center',fontWeight:600}}>{upsellQtys[u.id]||0}</span>
                  <button onClick={()=>setUpsellQtys(p=>({...p,[u.id]:(p[u.id]||0)+1}))} style={{width:28,height:28,borderRadius:6,border:'1px solid #D85A30',background:'#FAECE7',cursor:'pointer',fontSize:16,color:'#D85A30'}}>+</button>
                </div>
              </div>
            ))}
            <div style={{display:'flex',gap:8,marginTop:6}}>
              <button className="btn" style={{flex:1}} onClick={()=>finishPrintKOT(false)}>Skip & Print</button>
              <button className="btn btn-primary" style={{flex:1.5}} onClick={()=>finishPrintKOT(Object.values(upsellQtys).some(q=>q>0))}>
                {Object.values(upsellQtys).some(q=>q>0)?'🛒 Add & Print':'🖨️ Print KOT'}
              </button>
            </div>
          </div>
        </div>
      )}

      {billActionPopup && (
        <div className="modal-overlay">
          <div style={{background:'#fff',borderRadius:14,padding:22,width:360,boxShadow:'0 8px 30px rgba(0,0,0,.15)'}}>
            <div style={{fontWeight:700,fontSize:16,marginBottom:4}}>🧾 Print Bill</div>
            <div style={{fontSize:13,color:'#888',marginBottom:16}}>Print 2 copies of the bill, or choose None to carry on without printing.</div>
            <div style={{display:'flex',flexDirection:'column',gap:8}}>
              <button className="btn btn-primary" onClick={()=>finishPrintBillAction('print')}>🖨️ Print Bill (2 copies)</button>
              <button className="btn" onClick={()=>finishPrintBillAction('none')}>None</button>
              <button className="btn" onClick={()=>setBillActionPopup(false)} style={{background:'transparent',border:'none',color:'#999',fontSize:12}}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {settleConfirm && (
        <div className="modal-overlay">
          <div style={{background:'#fff',borderRadius:14,padding:22,width:340,boxShadow:'0 8px 30px rgba(0,0,0,.15)'}}>
            <div style={{fontWeight:700,fontSize:16,marginBottom:6}}>Settle this bill?</div>
            <div style={{fontSize:13,color:'#666',marginBottom:18}}>Are you sure you want to settle {orderLabel} — ₹{fmt(total)}?</div>
            <div style={{display:'flex',gap:8}}>
              <button className="btn" style={{flex:1}} onClick={()=>setSettleConfirm(false)}>No</button>
              <button className="btn btn-primary" style={{flex:1}} onClick={()=>{ setSettleConfirm(false); commitOrder(); }}>Yes, Settle</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}